import { createContext, useContext, ReactNode, useCallback, useEffect, useState } from 'react';
import { Spinner } from '@flixbus/honeycomb-react';

import { getBatch } from '../../apis';

import styles from './voucher-batch.module.scss';
import { Batch } from '../../types/Voucher';

interface BatchContextType {
  data: Batch;
  isPending: boolean;
  error: Error | null;
  complete: boolean;
}

const BatchContext = createContext<BatchContextType | undefined>(undefined);

export const useBatch = () => {
  const context = useContext(BatchContext);
  if (!context) {
    throw new Error('useBatch must be used within a BatchProvider');
  }
  return context;
};

interface BatchProviderProps {
  id: string;
  children: ReactNode;
}

export const BatchProvider = ({ id, children }: BatchProviderProps) => {
  const [data, setData] = useState<Batch | null>(null);
  const [isPending, setIsPending] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [complete, setComplete] = useState(false);

  const fetchBatch = useCallback(async () => {
    try {
      const response = await getBatch(id);
      setData(response);
      setComplete(response.remainingSize === 0);
    } catch (err) {
      setError(err as Error);
    } finally {
      setIsPending(false);
    }
  }, [id]);

  useEffect(() => {
    fetchBatch();

    if (data?.remainingSize === 0) return;

    const interval = setInterval(() => {
      fetchBatch();
    }, 1000);

    return () => clearInterval(interval);
  }, [fetchBatch, data?.remainingSize]);

  if (error) {
    return <div>Something went wrong 🥲</div>;
  }

  if (!data || isPending) {
    return <Spinner extraClasses={styles.spinner} size={'xl'} />;
  }

  return (
    <BatchContext.Provider value={{ data, isPending, error, complete }}>
      {children}
    </BatchContext.Provider>
  );
};
